'use client';

import { createContext } from 'react';

import type { CheckoutLifecycleEvent } from './CheckoutLifecycleEvent';

export type CheckoutLifecycleListener = (event: CheckoutLifecycleEvent) => void;

export interface CheckoutNotificationContextValue {
  /** Announce a checkout lifecycle event to every subscriber (the checkout layer + modal chrome call this). */
  notify: (event: CheckoutLifecycleEvent) => void;
  /** Register a listener; returns an unsubscribe. */
  subscribe: (listener: CheckoutLifecycleListener) => () => void;
  /**
   * Synchronously dispatch `event` to willNotify listeners — the pre-dispatch the
   * close handler ({@link useCloseCheckoutModal}) runs BEFORE its default
   * `router.back()`, so a consumer can call `event.preventDefault()` to claim
   * navigation. Kept separate from {@link notify} so a preventDefault vote never
   * triggers a consumer's real terminal-event side effects (which run on `notify`).
   */
  willNotify: (event: CheckoutLifecycleEvent) => void;
  /** Register a willNotify listener; returns an unsubscribe. */
  subscribeWillNotify: (listener: CheckoutLifecycleListener) => () => void;
}

/**
 * App-wide checkout lifecycle notification hub — see {@link CheckoutNotificationProvider}.
 * `null` when read outside the provider so consumer hooks can no-op gracefully.
 */
export const CheckoutNotificationContext =
  createContext<CheckoutNotificationContextValue | null>(null);
